import { DynamoDBClient, QueryCommand } from "@aws-sdk/client-dynamodb";
import { unmarshall } from "@aws-sdk/util-dynamodb";
import { useQuery } from "@tanstack/vue-query";
import { ComputedRef, computed } from "vue";
import type { ProjectMetadata } from "../../lib/dynamodb/types";

/**
 * Get project metadata
 */
export async function getProjectMetadata(
	projectId: string,
	dynamodb: {
		user_table_name: string;
		client: DynamoDBClient;
	},
): Promise<ProjectMetadata> {
	const { client, user_table_name } = dynamodb;

	const command = new QueryCommand({
		TableName: user_table_name,
		KeyConditionExpression: "PK = :pk AND SK = :sk",
		ProjectionExpression: "project_id, project_name, created_at, updated_at",
		ExpressionAttributeValues: {
			":pk": { S: `PROJECT#${projectId}` },
			":sk": { S: "METADATA" },
		},
	});

	try {
		const result = await client.send(command);
		if (!result.Items || result.Items.length === 0) {
			throw new Error(`Project metadata not found: ${projectId}`);
		}

		const unmarshalled = unmarshall(result.Items[0]);
		return {
			project_id: unmarshalled.project_id || projectId,
			project_name: unmarshalled.project_name || projectId,
			created_at: unmarshalled.created_at || new Date().toISOString(),
			updated_at: unmarshalled.updated_at || new Date().toISOString(),
		};
	} catch (err) {
		throw err;
	}
}

export function useProjectMetadata(
	projectId: ComputedRef<string | null>,
	dynamodb: ComputedRef<{
		user_table_name: string;
		client: DynamoDBClient;
	} | null>,
	credentialsReset: ComputedRef<number>,
) {
	const enabled = computed(() => !!projectId.value && !!dynamodb.value);

	return useQuery({
		queryKey: ["project-metadata", projectId, credentialsReset],
		queryFn: ({ queryKey: [_, pId] }) =>
			getProjectMetadata(
				pId as string,
				dynamodb.value as {
					user_table_name: string;
					client: DynamoDBClient;
				},
			),
		enabled,
	});
}
